import { datesInclusive, daysBetween, parseIsoDate, weekday } from "./date.ts";
import {
  DomainError,
  isSelectableOutcome,
  type IsoDate,
  type MatrixDocument,
  type Outcome,
  type SelectableOutcome,
} from "./model.ts";
import { scoreDay } from "./scoring.ts";

export const MAX_OUTCOME_BATCH_CELLS = 5_000;
export const MAX_OUTCOME_BATCH_CANDIDATE_CELLS = 250_000;

export type OutcomeBatchAction = SelectableOutcome | "clear";
export type OutcomeBatchDateScope = "due" | "all";
export type OutcomeBatchCellMode = "fill-blanks" | "replace-existing";

export interface OutcomeBatchInput {
  readonly operationIds: readonly string[];
  readonly from: IsoDate;
  readonly to: IsoDate;
  /** Trusted present used to distinguish unresolved, settled, and projected cells. */
  readonly asOf: IsoDate;
  readonly action: OutcomeBatchAction;
  readonly dateScope?: OutcomeBatchDateScope;
  readonly cellMode?: OutcomeBatchCellMode;
  /** Sunday=0 through Saturday=6. null means every weekday. */
  readonly weekdays?: readonly number[] | null;
}

export interface OutcomeBatchCell {
  readonly operationId: string;
  readonly date: IsoDate;
  readonly before: Outcome | null;
  readonly after: SelectableOutcome | null;
}

export interface OutcomeBatchPreview {
  readonly input: Required<Omit<OutcomeBatchInput, "weekdays">> & { readonly weekdays: readonly number[] | null };
  readonly cells: readonly OutcomeBatchCell[];
  /** Frozen snapshots make preview/apply exact while allowing a complete retry. */
  readonly beforeFingerprint: string;
  readonly afterFingerprint: string;
  readonly skippedExisting: number;
  readonly skippedEmpty: number;
  readonly skippedGeneratedBye: number;
  readonly skippedImportOnly: number;
  readonly skippedNotDue: number;
  readonly skippedInactive: number;
  readonly skippedWeekday: number;
}

function cellKey(operationId: string, date: IsoDate): string {
  return `${operationId}\u0000${date}`;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "undefined";
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  const record = value as Readonly<Record<string, unknown>>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`).join(",")}}`;
}

/** Collision-free canonical snapshot used to freeze exact local previews.
 * Trust-boundary workflows replace this value with a server-generated SHA-256 digest. */
export function outcomeBatchDocumentFingerprint(document: MatrixDocument): string {
  return `outcome-document:${stableSerialize(document)}`;
}

interface OutcomeCellWrite {
  readonly operationId: string;
  readonly date: IsoDate;
  readonly after: Outcome | null;
}

function applyCellsUnchecked(document: MatrixDocument, cells: readonly OutcomeCellWrite[]): MatrixDocument {
  const targets = new Map(cells.map((cell) => [cellKey(cell.operationId, cell.date), cell]));
  const entries = document.entries.filter((entry) => !targets.has(cellKey(entry.operationId, entry.date)));
  for (const cell of cells) {
    if (cell.after !== null) entries.push({ operationId: cell.operationId, date: cell.date, outcome: cell.after });
  }
  return { ...document, entries };
}

function normalizedInput(input: OutcomeBatchInput): OutcomeBatchPreview["input"] {
  parseIsoDate(input.from);
  parseIsoDate(input.to);
  parseIsoDate(input.asOf);
  if (input.to < input.from) throw new DomainError("INVALID_BATCH_RANGE", "batch end must be on or after its start");
  if (input.action !== "clear" && !isSelectableOutcome(input.action)) {
    throw new DomainError("INVALID_BATCH_ACTION", "batch action must be T, W, C, B, or clear");
  }
  const dateScope = input.dateScope ?? "due";
  if (dateScope !== "due" && dateScope !== "all") {
    throw new DomainError("INVALID_BATCH_DATE_SCOPE", "batch date scope must be due or all");
  }
  const cellMode = input.cellMode ?? "fill-blanks";
  if (cellMode !== "fill-blanks" && cellMode !== "replace-existing") {
    throw new DomainError("INVALID_BATCH_CELL_MODE", "batch cell mode must be fill-blanks or replace-existing");
  }
  const operationIds = [...new Set(input.operationIds)];
  if (operationIds.length === 0) throw new DomainError("EMPTY_BATCH_SELECTION", "select at least one operation");
  if (operationIds.length * (daysBetween(input.from, input.to) + 1) > MAX_OUTCOME_BATCH_CANDIDATE_CELLS) {
    throw new DomainError("OUTCOME_BATCH_SELECTION_TOO_LARGE", `an outcome batch can inspect at most ${MAX_OUTCOME_BATCH_CANDIDATE_CELLS} candidate cells`);
  }
  const weekdays = input.weekdays === undefined || input.weekdays === null ? null : [...new Set(input.weekdays)].sort();
  if (weekdays !== null && (weekdays.length === 0 || weekdays.some((day) => !Number.isInteger(day) || day < 0 || day > 6))) {
    throw new DomainError("INVALID_BATCH_WEEKDAYS", "weekdays must use Sunday=0 through Saturday=6");
  }
  return {
    operationIds,
    from: input.from,
    to: input.to,
    asOf: input.asOf,
    action: input.action,
    dateScope,
    cellMode,
    weekdays,
  };
}

/** Deterministically selects the exact cells that a protected batch may commit. */
export function previewOutcomeBatch(document: MatrixDocument, rawInput: OutcomeBatchInput): OutcomeBatchPreview {
  const input = normalizedInput(rawInput);
  const known = new Set(document.operations.map((operation) => operation.id));
  const unknown = input.operationIds.find((operationId) => !known.has(operationId));
  if (unknown) throw new DomainError("UNKNOWN_OPERATION", `Unknown batch operation ${unknown}`);
  const entries = new Map(document.entries.map((entry) => [cellKey(entry.operationId, entry.date), entry.outcome]));
  const cells: OutcomeBatchCell[] = [];
  let skippedExisting = 0;
  let skippedEmpty = 0;
  let skippedGeneratedBye = 0;
  let skippedImportOnly = 0;
  let skippedNotDue = 0;
  let skippedInactive = 0;
  let skippedWeekday = 0;

  for (const operationId of input.operationIds) {
    for (const date of datesInclusive(input.from, input.to)) {
      if (input.weekdays !== null && !input.weekdays.includes(weekday(date))) { skippedWeekday += 1; continue; }
      const contribution = scoreDay(document, date, { operationIds: [operationId], asOf: input.asOf }).contributions[0];
      if (!contribution || contribution.applicability === "inactive") { skippedInactive += 1; continue; }
      if (input.dateScope === "due" && contribution.applicability !== "due") { skippedNotDue += 1; continue; }
      const before = entries.get(cellKey(operationId, date));
      if (input.action === "clear") {
        if (before === undefined) { skippedEmpty += 1; continue; }
        if (before === "M") { skippedImportOnly += 1; continue; }
        cells.push({ operationId, date, before, after: null });
      } else {
        if (
          input.action === "B" &&
          before === undefined &&
          contribution.displaySource === "generated-schedule"
        ) {
          skippedGeneratedBye += 1;
          continue;
        }
        if (input.cellMode === "fill-blanks" && before !== undefined) { skippedExisting += 1; continue; }
        if (before === input.action) { skippedExisting += 1; continue; }
        cells.push({ operationId, date, before: before ?? null, after: input.action });
      }
      if (cells.length > MAX_OUTCOME_BATCH_CELLS) {
        throw new DomainError("OUTCOME_BATCH_TOO_LARGE", `an outcome batch can affect at most ${MAX_OUTCOME_BATCH_CELLS} cells`);
      }
    }
  }

  const beforeFingerprint = outcomeBatchDocumentFingerprint(document);
  const afterFingerprint = outcomeBatchDocumentFingerprint(applyCellsUnchecked(document, cells));
  return {
    input,
    cells,
    beforeFingerprint,
    afterFingerprint,
    skippedExisting,
    skippedEmpty,
    skippedGeneratedBye,
    skippedImportOnly,
    skippedNotDue,
    skippedInactive,
    skippedWeekday,
  };
}

/** Applies an exact frozen preview, or recognizes a complete retry as success. */
export function applyOutcomeBatch(document: MatrixDocument, preview: OutcomeBatchPreview): MatrixDocument {
  if (preview.cells.length > MAX_OUTCOME_BATCH_CELLS) {
    throw new DomainError("OUTCOME_BATCH_TOO_LARGE", `an outcome batch can affect at most ${MAX_OUTCOME_BATCH_CELLS} cells`);
  }
  const currentFingerprint = outcomeBatchDocumentFingerprint(document);
  if (currentFingerprint === preview.afterFingerprint) return document;
  if (currentFingerprint !== preview.beforeFingerprint) {
    throw new DomainError("OUTCOME_BATCH_CONFLICT", "the document changed before the batch could apply");
  }
  const applied = applyCellsUnchecked(document, preview.cells);
  if (outcomeBatchDocumentFingerprint(applied) !== preview.afterFingerprint) {
    throw new DomainError("OUTCOME_BATCH_INVALID_PREVIEW", "the batch preview no longer matches its frozen result");
  }
  return applied;
}

/** Converts one computed schedule Bye into an explicit stored manual Bye. */
export function storeGeneratedByeAsManual(
  document: MatrixDocument,
  operationId: string,
  date: IsoDate,
  asOf: IsoDate,
): MatrixDocument {
  parseIsoDate(date);
  parseIsoDate(asOf);
  if (!document.operations.some((operation) => operation.id === operationId)) {
    throw new DomainError("UNKNOWN_OPERATION", `Unknown operation ${operationId}`);
  }
  const contribution = scoreDay(document, date, { operationIds: [operationId], asOf }).contributions[0];
  if (contribution?.displaySource !== "generated-schedule") {
    throw new DomainError("NOT_GENERATED_BYE", "only a computed schedule Bye can be stored as a manual Bye");
  }
  return {
    ...document,
    entries: [...document.entries, { operationId, date, outcome: "B" }],
  };
}

/** Restores only batch-written cells after verifying every one remains unchanged. */
export function undoOutcomeBatch(document: MatrixDocument, preview: OutcomeBatchPreview): MatrixDocument {
  const current = new Map(document.entries.map((entry) => [cellKey(entry.operationId, entry.date), entry.outcome]));
  for (const cell of preview.cells) {
    if ((current.get(cellKey(cell.operationId, cell.date)) ?? null) !== cell.after) {
      throw new DomainError("OUTCOME_BATCH_UNDO_CONFLICT", "a batch-written cell changed before undo");
    }
  }
  const restored = preview.cells.map((cell) => ({ ...cell, after: cell.before }));
  return applyCellsUnchecked(document, restored);
}
